import { useTranslation } from 'react-i18next';
import { useMemo, useState } from 'react';
import { AvailableDimension, AvailableMember, UUID, useDimensionsForConcept } from 'entities/tag';

interface SummandScopingRowProps {
  summandConceptName: string;
  documentSuperId: number | undefined;
  value: { axisConceptId: UUID; memberConceptId: UUID }[];
  onChange: (next: { axisConceptId: UUID; memberConceptId: UUID }[]) => void;
}

interface FlatMember {
  conceptId: UUID;
  qname: string;
  label: string;
  isDefault: boolean;
  usable: boolean;
}

interface FlatAxis {
  conceptId: UUID;
  qname: string;
  label: string;
  members: FlatMember[];
}

function pickLabel(labels: unknown, fallback: string): string {
  const flat = labels as Record<string, string | undefined> | undefined;
  return flat?.['en'] ?? flat?.['sv'] ?? flat?.['fallback'] ?? fallback;
}

function flattenMembers(axis: AvailableDimension): FlatMember[] {
  const seen = new Set<UUID>();
  const out: FlatMember[] = [];
  function walk(member: AvailableMember): void {
    if (member.conceptId && !seen.has(member.conceptId)) {
      seen.add(member.conceptId);
      out.push({
        conceptId: member.conceptId,
        qname: member.id,
        label: pickLabel(member.labels, member.id),
        isDefault: axis.defaultMemberId !== null && member.id === axis.defaultMemberId,
        usable: member.usable !== false,
      });
    }
    for (const c of member.children) walk(c);
  }
  for (const domain of axis.domains) walk(domain.members);
  return out;
}

export const SummandScopingRow: React.FC<SummandScopingRowProps> = ({ summandConceptName, documentSuperId, value, onChange }) => {
  const { t } = useTranslation('tag');

  const [expanded, setExpanded] = useState(value.length > 0);
  const { data: dimensionsResponse } = useDimensionsForConcept(documentSuperId, summandConceptName);

  // Flatten every axis in any applicable hypercube. Dedup by axis conceptId
  // so the same axis appearing in multiple hypercubes shows up once.
  const axes = useMemo<FlatAxis[]>(() => {
    if (!dimensionsResponse) return [];
    const seen = new Map<UUID, FlatAxis>();
    for (const hc of dimensionsResponse.hypercubes) {
      for (const dim of hc.dimensions) {
        if (!dim.conceptId) continue;
        if (seen.has(dim.conceptId)) continue;
        seen.set(dim.conceptId, {
          conceptId: dim.conceptId,
          qname: dim.id,
          label: pickLabel(dim.labels, dim.id),
          // Defaults are implied by omission and usable="false" members are
          // structural nodes — neither can appear in a real iXBRL context,
          // so neither is offered as a scoping target (server rejects both
          // too: DEFAULT_MEMBER_NOT_SCOPABLE / INVALID_SCOPING_PAIR).
          members: flattenMembers(dim).filter((m) => !m.isDefault && m.usable),
        });
      }
    }
    return [...seen.values()];
  }, [dimensionsResponse]);

  const axisById = useMemo(() => new Map(axes.map((a) => [a.conceptId, a])), [axes]);

  const handleAxisChange = (idx: number, axisConceptId: UUID) => {
    const next = [...value];
    next[idx] = { axisConceptId, memberConceptId: '' as UUID };
    onChange(next);
  };

  const handleMemberChange = (idx: number, memberConceptId: UUID) => {
    const next = [...value];
    next[idx] = { ...next[idx], memberConceptId };
    onChange(next);
  };

  const handleAdd = () => onChange([...value, { axisConceptId: '' as UUID, memberConceptId: '' as UUID }]);
  const handleRemove = (idx: number) => onChange(value.filter((_, i) => i !== idx));

  if (!summandConceptName) {
    // No summand concept picked yet — scoping isn't authorable until we know
    // which axes are applicable.
    return null;
  }

  return (
    <div style={{ marginLeft: 24, marginTop: 4 }}>
      <button
        type="button"
        onClick={() => setExpanded((v) => !v)}
        style={{
          background: 'none',
          border: 'none',
          padding: '2px 0',
          color: '#717077',
          fontSize: 12,
          fontWeight: 400,
          textTransform: 'none',
          textDecoration: 'none',
          cursor: 'pointer',
          display: 'inline-flex',
          alignItems: 'center',
          gap: 6,
        }}
      >
        <i className={`fa fa-caret-${expanded ? 'down' : 'right'}`} />
        <span>
          {t('extension.scoping.toggle')}
          {value.length > 0 ? ` (${value.length})` : ''}
        </span>
      </button>

      {expanded &&
        value.map((q, idx) => {
          const axis = axisById.get(q.axisConceptId);
          return (
            // Stack axis and member on top of each other inside the row to
            // give each dropdown the modal's full width; labels in ESEF can
            // run 40+ characters and look awful when crushed side-by-side.
            <div key={idx} style={{ marginTop: 6, padding: 8, backgroundColor: '#f6f8fa', borderRadius: 4 }}>
              <div className="flex-row" style={{ gap: 8, alignItems: 'flex-start' }}>
                <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 4 }}>
                  <select
                    className="form-select form-select-sm"
                    value={q.axisConceptId}
                    onChange={(e) => handleAxisChange(idx, e.target.value as UUID)}
                    aria-label={`Scoping axis ${idx + 1}`}
                    style={{ width: '100%' }}
                  >
                    <option value="">{t('extension.scoping.axis-placeholder')}</option>
                    {axes.map((a) => (
                      <option key={a.conceptId} value={a.conceptId}>
                        {a.label}
                      </option>
                    ))}
                  </select>
                  <select
                    className="form-select form-select-sm"
                    value={q.memberConceptId}
                    onChange={(e) => handleMemberChange(idx, e.target.value as UUID)}
                    disabled={!q.axisConceptId}
                    aria-label={`Scoping member ${idx + 1}`}
                    style={{ width: '100%' }}
                  >
                    <option value="">{t('extension.scoping.member-placeholder')}</option>
                    {(axis?.members ?? []).map((m) => (
                      <option key={m.conceptId} value={m.conceptId}>
                        {m.label}
                      </option>
                    ))}
                  </select>
                </div>
                <button
                  type="button"
                  className="btn btn-link btn-sm"
                  onClick={() => handleRemove(idx)}
                  aria-label={`Remove scoping condition ${idx + 1}`}
                  style={{ padding: '4px 6px', alignSelf: 'flex-start' }}
                >
                  <i className="fa fa-times" />
                </button>
              </div>
            </div>
          );
        })}

      {expanded && (
        <button type="button" className="btn btn-link btn-sm" onClick={handleAdd} style={{ paddingLeft: 0, marginTop: 4 }}>
          + {t('extension.scoping.add-condition')}
        </button>
      )}
    </div>
  );
};
